import api from 'api';
import classNames from 'classnames';
import ResolveMultiSelectField from 'components/ResolveMultiSelectField';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import useCallbackRef from 'hooks/useCallbackRef';
import { Contact, AddedContactToDeals } from 'models/Contact';
import {
  ENQUIRY_ORIGIN,
  EnquiryCreationInitiatedEventProps,
} from 'models/MixpanelEvents';
import { Button, Loader } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import mixpanelService from 'services/mixpanelService';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import validations from 'validations';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { getArrayLength, getHasElementsInArray, getIsArrayEmpty } from 'utils';
import { AVAILABILITY_STATE } from 'router/subrouters/DataRoom/models/DataRoomBulk';

import './ContactsManageDrawer.styles.scss';

export type ManageDrawerRefProps = SideDrawerRef & {
  setContacts: (contacts: Contact[]) => void;
};

type ContactsManageDrawerProps = {
  className?: string;
  onClose?: () => void;
};

const ContactsManageDrawer: React.ForwardRefRenderFunction<
  ManageDrawerRefProps,
  ContactsManageDrawerProps
> = (props, ref) => {
  const { className } = props;

  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deals, setDeals] = useState<AddedContactToDeals[]>([]);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => ({ ...drawer, setContacts }), [drawer]);

  const contactsIds = useMemo(() => contacts.map(({ _id }) => _id), [contacts]);

  const classes = classNames('contacts-manage-drawer', className);
  const { t } = useTranslation();

  const contactsLength = getArrayLength(contactsIds);

  const getMixpanelProps = useGetMixpanelProps();

  const dealRoomIdsOptions = useMemo(
    () =>
      deals.map(({ dealRoomId, dealRoomName, state }) => ({
        value: dealRoomId,
        label: dealRoomName,
        disabled: state === AVAILABILITY_STATE.ON,
        state,
      })),
    [deals],
  );

  const initialDealRoomIds = useMemo(
    () =>
      deals
        .filter(({ state }) => state === AVAILABILITY_STATE.ON)
        .map(({ dealRoomId }) => dealRoomId),
    [deals],
  );

  const initialValues = useMemo(
    () => ({
      dealRoomIds: initialDealRoomIds,
    }),
    [initialDealRoomIds],
  );

  const hasInitialValuesMixed = deals.some(
    ({ state }) => state === AVAILABILITY_STATE.MIXED,
  );

  const handleOnClose = useCallback(() => drawer?.setOpen(false), [drawer]);

  const handleOnSubmit = useCallback(
    async ({ dealRoomIds }) => {
      if (getIsArrayEmpty(dealRoomIds)) {
        handleOnClose?.();
        return;
      }

      try {
        dealRoomIds.forEach((dealId: string) => {
          const dealName = deals.find(
            ({ dealRoomId }) => dealId === dealRoomId,
          )?.dealRoomName;

          mixpanelService.enquiryCreationInitiated(
            getMixpanelProps({
              dealId,
              dealName,
              enquiryOrigin: ENQUIRY_ORIGIN.CONTACTS,
            }) as EnquiryCreationInitiatedEventProps,
          );
        });

        await api.contacts.addContactsToDeals(contactsIds, dealRoomIds);
        popSuccess(
          t('successfullyAddedContactsToDeals', {
            count: getArrayLength(dealRoomIds),
          }),
        );
        handleOnClose?.();
      } catch (e) {
        popServerError(e);
      }
    },
    [
      contactsIds,
      deals,
      getMixpanelProps,
      handleOnClose,
      popServerError,
      popSuccess,
      t,
    ],
  );

  useEffect(() => {
    (async () => {
      if (!drawer?.open) return;

      try {
        setIsLoading(true);
        const { data } = await api.contacts.getAvailableDeals(contactsIds);
        setDeals(data);
      } catch (e) {
        popServerError(e);
        handleOnClose?.();
      } finally {
        setIsLoading(false);
      }
    })();
  }, [contactsIds, drawer?.open, handleOnClose, popServerError]);

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      title={t('whatDealsToAddContacts', { count: contactsLength })}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <Form
          initialValues={initialValues}
          onSubmit={handleOnSubmit}
          render={({ handleSubmit, submitting, invalid }) => (
            <form onSubmit={handleSubmit}>
              {hasInitialValuesMixed && (
                <p>{t('dealsMarkerdWithDash', { count: contactsLength })}</p>
              )}

              {getHasElementsInArray(deals) ? (
                <>
                  <div className="white-panel">
                    <Field
                      name="dealRoomIds"
                      searchable
                      hasCheckbox
                      hasSelectedOptions={false}
                      className="contacts-manage-drawer__multi-select"
                      component={ResolveMultiSelectField}
                      options={dealRoomIdsOptions}
                      validate={validations.requireOneFromArray()}
                    />
                  </div>

                  <Button type="submit" disabled={submitting || invalid}>
                    {t('confirm')}
                  </Button>
                </>
              ) : (
                <p>{t('youDontHaveDeals')}</p>
              )}

              {submitting && <Loader />}
            </form>
          )}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(ContactsManageDrawer);
